import { ExchangeRates, UnavailableDateError } from '~/Api/User/ExchangeRate';

import { Action, ActionType } from './Action';

interface Rates {
	request: Promise<any> | null;
	userId: string | null;
	groupId: string | null;
	type: string | null;
	date: Date | null;
	rates: ExchangeRates[] | null;
	isFetching: boolean;
	unavailableDateError: UnavailableDateError | null;
}

export interface State {
	createRequest: Promise<any> | null;
	updateRequest: Promise<any> | null;
	updateGroupRatesRequest: Promise<any> | null;
	allRates: Rates | null;
	historicalRates: Rates | null;
}

function createState(): State {
	return {
		createRequest: null,
		updateRequest: null,
		updateGroupRatesRequest: null,
		allRates: null,
		historicalRates: null,
	};
}

export default function reducer(state: State = createState(), action: Action): State {
	switch (action.type) {
		case ActionType.request: {
			const newState = { ...state };
			const rates = {
				request: action.request,
				userId: action.userId,
				groupId: action.groupId,
				type: action.rateType,
				date: action.date,
				rates: null,
				isFetching: true,
				unavailableDateError: null,
			};

			if (action.date !== null) {
				newState.historicalRates = rates;
			} else {
				newState.allRates = rates;
			}

			return newState;
		}

		case ActionType.receive: {
			const currentRates = action.date !== null ? state.historicalRates : state.allRates;
			if (currentRates === null || action.request !== currentRates?.request) {
				return state;
			}

			const newState = { ...state };
			const rates = {
				...currentRates,
				rates: action.rates,
				isFetching: false,
				unavailableDateError: action.unavailableDateError,
			};

			if (action.date !== null) {
				newState.historicalRates = rates;
			} else {
				newState.allRates = rates;
			}

			return newState;
		}

		case ActionType.requestCreate:
			return { ...state, createRequest: action.request };

		case ActionType.receiveCreate:
			if (action.request !== state.createRequest) {
				return state;
			}
			return {
				...state,
				createRequest: null,
				allRates:
					state.allRates && state.allRates.rates && action.rates
						? {
								...state.allRates,
								rates: [...state.allRates.rates, action.rates],
						  }
						: state.allRates,
			};

		case ActionType.requestUpdate:
			return { ...state, updateRequest: action.request };

		case ActionType.receiveUpdate:
			if (action.request !== state.updateRequest) {
				return state;
			}

			const { rates } = action;

			return {
				...state,
				updateRequest: null,
				allRates:
					state.allRates !== null && state.allRates.rates !== null && rates !== null
						? {
								...state.allRates,
								rates: state.allRates.rates.map((rate) => (rate.id === rates.id ? rates : rate)),
						  }
						: state.allRates,
			};

		case ActionType.requestUpdateGroupRates:
			return { ...state, updateGroupRatesRequest: action.request };

		case ActionType.receiveUpdateGroupRates:
			return action.request === state.updateGroupRatesRequest ? { ...state, updateGroupRatesRequest: null } : state;

		default:
			return state;
	}
}

export function selectRates(
	state: State,
	userId: string,
	groupId: string,
	type: string | null = null,
	date: Date | null = null
): ExchangeRates[] | null {
	const rates = selectRatesRecord(state, userId, groupId, type, date);
	return rates ? rates.rates : null;
}

export function selectSingleRates(
	state: State,
	userId: string,
	groupId: string,
	type: string | null = null,
	date: Date | null = null
): ExchangeRates | null {
	const rates = selectRates(state, userId, groupId, type, date);
	return rates ? rates[0] : null;
}

export function selectUnavailableDateError(
	state: State,
	userId: string,
	groupId: string,
	type: string | null = null,
	date: Date | null = null
): UnavailableDateError | null {
	const rates = selectRatesRecord(state, userId, groupId, type, date);
	return rates ? rates.unavailableDateError : null;
}

function selectRatesRecord(
	state: State,
	userId: string,
	groupId: string,
	type: string | null = null,
	date: Date | null = null
): Rates | null {
	const rates = date !== null ? state.historicalRates : state.allRates;
	if (rates && rates.userId === userId && rates.groupId === groupId && rates.type === type && rates.date === date) {
		return rates;
	}
	return null;
}
